'use client';

import * as React from 'react';
import { clsx } from 'clsx';
import { Tag } from './Tag';
import styles from './TagFilter.module.css';

export interface TagFilterOption {
  id: string | number;
  label: string;
}

export interface TagFilterProps {
  /** Available options to select from */
  options: TagFilterOption[];
  /** Currently selected options */
  value: TagFilterOption[];
  /** Called when selection changes */
  onChange: (value: TagFilterOption[]) => void;
  /** Placeholder text shown when nothing is selected */
  placeholder?: string;
  /** Label above the control */
  label?: string;
  /** Full width */
  fullWidth?: boolean;
  /** Additional class names */
  className?: string;
}

function ChevronIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

export function TagFilter({
  options,
  value,
  onChange,
  placeholder = 'Filter by tags...',
  label,
  fullWidth = false,
  className,
}: TagFilterProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const rootRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const selectedIds = new Set(value.map((v) => v.id));

  const filtered = options.filter(
    (o) =>
      !selectedIds.has(o.id) === false
        ? true
        : o.label.toLowerCase().includes(search.toLowerCase())
  );

  const visibleOptions = options.filter((o) =>
    o.label.toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (option: TagFilterOption) => {
    if (selectedIds.has(option.id)) {
      onChange(value.filter((v) => v.id !== option.id));
    } else {
      onChange([...value, option]);
    }
    setSearch('');
    inputRef.current?.focus();
  };

  const remove = (id: string | number) => {
    onChange(value.filter((v) => v.id !== id));
  };

  React.useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div ref={rootRef} className={clsx(styles.root, fullWidth && styles.fullWidth, className)}>
      {label && <label className={styles.label}>{label}</label>}

      <div
        className={clsx(styles.control, open && styles.controlFocused)}
        onClick={() => { setOpen(true); inputRef.current?.focus(); }}
      >
        {value.map((v) => (
          <Tag key={v.id} variant="minimal" size="sm" removable onRemove={() => remove(v.id)}>
            {v.label}
          </Tag>
        ))}
        <input
          ref={inputRef}
          className={styles.input}
          value={search}
          placeholder={value.length === 0 ? placeholder : ''}
          onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
        />
        <span className={clsx(styles.chevron, open && styles.chevronOpen)}>
          <ChevronIcon />
        </span>
      </div>

      {open && (
        <div className={styles.dropdown}>
          {visibleOptions.length === 0 ? (
            <div className={styles.empty}>No options found</div>
          ) : (
            visibleOptions.map((option) => (
              <button
                key={option.id}
                className={clsx(styles.option, selectedIds.has(option.id) && styles.optionSelected)}
                onMouseDown={(e) => { e.preventDefault(); toggle(option); }}
              >
                {option.label}
                {selectedIds.has(option.id) && (
                  <span className={styles.checkIcon}><CheckIcon /></span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default TagFilter;
